/**
 * Utility functions for event handlers
 * These functions are extracted from inline handlers to comply with CSP
 */

/**
 * Creates a handler for form field changes
 * @param {Function} setForm - State setter function
 * @param {string} field - Field name to update
 * @returns {Function} Event handler function
 */
export const createFormFieldHandler = (setForm, field) => {
  return (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };
};

/**
 * Creates a handler for nested form field changes
 * @param {Function} setForm - State setter function
 * @param {string} parentField - Parent field name (e.g., 'training', 'buildingDetails')
 * @param {string} childField - Child field name
 * @returns {Function} Event handler function
 */
export const createNestedFormFieldHandler = (setForm, parentField, childField) => {
  return (e) => {
    setForm(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: e.target.value
      }
    }));
  };
};

/**
 * Creates a handler for select option changes
 * @param {Function} setForm - State setter function
 * @param {string} field - Field name to update
 * @param {string} clearField - Optional field to clear when this field changes
 * @returns {Function} Event handler function
 */
export const createSelectHandler = (setForm, field, clearField = null) => {
  return (opt) => {
    setForm(prev => ({
      ...prev,
      [field]: opt ? opt.value : '',
      ...(clearField ? { [clearField]: '' } : {})
    }));
  };
};

/**
 * Creates a handler for boolean toggle
 * @param {Function} setState - State setter function
 * @returns {Function} Event handler function
 */
export const createToggleHandler = (setState) => {
  return () => {
    setState(prev => !prev);
  };
};

/**
 * Creates a handler for page navigation
 * @param {Function} setCurrentPage - State setter function
 * @param {number} totalPages - Total number of pages
 * @param {string} direction - 'prev' or 'next'
 * @returns {Function} Event handler function
 */
export const createPageNavigationHandler = (setCurrentPage, totalPages, direction) => {
  return () => {
    setCurrentPage(prev => {
      if (direction === 'prev') {
        return Math.max(1, prev - 1);
      } else {
        return Math.min(totalPages, prev + 1);
      }
    });
  };
};

/**
 * Creates a handler for month navigation in week picker
 * @param {Function} setWeekPickerMonth - State setter function
 * @param {Date} weekPickerMonth - Current month
 * @param {string} direction - 'prev' or 'next'
 * @returns {Function} Event handler function
 */
export const createMonthNavigationHandler = (setWeekPickerMonth, weekPickerMonth, direction) => {
  return () => {
    const newMonth = new Date(weekPickerMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setWeekPickerMonth(newMonth);
  };
};

/**
 * Creates a handler for week selection
 * @param {Function} setForm - State setter function
 * @param {Function} setShowWeekPicker - State setter function
 * @param {string} weekValue - Selected week value
 * @returns {Function} Event handler function
 */
export const createWeekSelectionHandler = (setForm, setShowWeekPicker, weekValue) => {
  return () => {
    setForm(prev => ({ ...prev, taskDate: weekValue }));
    setShowWeekPicker(false);
  };
};

